import { useCallback, useEffect, useMemo, useState, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import DocViewer, { DocViewerRenderers } from "@cyntler/react-doc-viewer";
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { usePatient } from '../contexts/PatientContext';
import { useSync } from '../contexts/SyncContext';
import { openFolder, copyToClipboard } from '../utils/fileOperations';
import {
  Alert,
  Autocomplete,
  Avatar,
  Backdrop,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Divider,
  FormControl,
  Grid,
  IconButton,
  InputAdornment,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  Tab,
  Tabs,
  TextField,
  Tooltip,
  Typography,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import FirstPageIcon from '@mui/icons-material/FirstPage';
import LastPageIcon from '@mui/icons-material/LastPage';
import EditIcon from '@mui/icons-material/Edit';
import AddIcon from '@mui/icons-material/Add';
import PrintIcon from '@mui/icons-material/Print';
import DeleteIcon from '@mui/icons-material/Delete';
import FolderIcon from '@mui/icons-material/Folder';
import FolderOpenIcon from '@mui/icons-material/FolderOpen';
import PersonIcon from '@mui/icons-material/Person';
import PhoneIcon from '@mui/icons-material/Phone';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import FullscreenIcon from '@mui/icons-material/Fullscreen';
import FullscreenExitIcon from '@mui/icons-material/FullscreenExit';
import RefreshIcon from '@mui/icons-material/Refresh';
import VisibilityIcon from '@mui/icons-material/Visibility';
import DownloadIcon from '@mui/icons-material/Download';
import SearchIcon from '@mui/icons-material/Search';
import { useAuth } from '../contexts/AuthContext';
import { normalizeGreekSearch } from '../utils/greekUtils';
import { createApiClient } from '../services/apiClient';
import { fetchPatients, updatePatient, buildPatientUpdatePayload, createPatient, deletePatient } from '../services/patientService';
import { normalizePatient } from '../utils/patientNormalizer';
import VisitsTab from '../components/patient-detail/VisitsTab';
import MedHistoryTab from '../components/patient-detail/MedHistoryTab';
import OperationsTab from '../components/patient-detail/OperationsTab';
import MedicationsTab from '../components/patient-detail/MedicationsTab';
import PatientFilesTab from '../components/patient-detail/PatientFilesTab';
import PatientGalleryTab from '../components/patient-detail/PatientGalleryTab';
import PatientEditDialog from '../components/PatientEditDialog';
import { 
  fetchPatientFiles, 
  getPatientFileSignedUrl,
  getLocalFilePreviewUrl 
} from '../services/patientFileService';
import { fetchFolderSettings } from '../services/folderService';
import FullReportPreviewDialog from '../components/patient-detail/FullReportPreviewDialog';
import { generateFullClinicalReport } from '../utils/reportGenerator';
import { medHistoryAPI, medicationAPI, operationAPI, patientFileAPI, systemAPI } from '../services/api';
import { fetchVisits } from '../services/visitService';
import { formatDate } from '../utils/dateUtils';
import axios from 'axios';

const resolvePatientId = (patient) =>
  patient?.__recordId ?? patient?.__originalId ?? patient?.ID_PT ?? patient?.id ?? null;

const usePatientFiles = (apiClient, patientId) => {
  const [files, setFiles] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const loadFiles = useCallback(async () => {
    if (!apiClient || !patientId) {
      return;
    }

    setLoading(true);
    setError('');
    try {
      const data = await fetchPatientFiles(apiClient, patientId);
      setFiles(Array.isArray(data) ? data : []);
    } catch (err) {
      setError(err.message ?? 'Failed to load patient files');
      setFiles([]);
    } finally {
      setLoading(false);
    }
  }, [apiClient, patientId]);

  useEffect(() => {
    loadFiles();
  }, [loadFiles]);

  return { files, loading, error, reload: loadFiles };
};

const usePatientFilePreview = (apiClient, localApiClient, patientFiles) => {
  const fetchSignedUrl = useCallback(
    async (id) => {
      if (!id) return null;

      // Hybrid Architecture Check: Look up file to see if it's local (absolute path on Mac)
      const file = (patientFiles || []).find(f => String(f.id_file ?? f.id) === String(id));
      const storagePath = file?.storage_path || file?.file_path;
      
      // Clinical logic: Absolute paths (starting with / or C:\) point to files on the clinical workstation.
      // Remote Hub files use relative paths (e.g. "123/file.pdf") in the fileurl column.
      // We only use the local-preview API if it's truly an absolute path AND it's not a Hub-managed file.
      const isAbsolute = (p) => p && (p.startsWith('/') || /^[a-zA-Z]:\\/.test(p));
      const isHubManaged = file?.fileurl && !isAbsolute(file.fileurl);
      
      if (storagePath && isAbsolute(storagePath) && !isHubManaged) {
        return { 
          url: getLocalFilePreviewUrl(localApiClient, storagePath), 
          fileType: file.file_type 
        };
      }

      // Default: Fetch from Remote Hub (Proxmox)
      if (!apiClient) return null;

      try {
        const { url, file_type: fileType } = await getPatientFileSignedUrl(apiClient, id);
        return { url, fileType: fileType || file?.file_type };
      } catch (err) {
        console.error('Failed to fetch preview URL:', err);
        return null;
      }
    },
    [apiClient, localApiClient, patientFiles]
  );

  const invalidate = useCallback(() => {
    // No-op now that we don't cache, but kept for API compatibility
  }, []);

  return { fetchSignedUrl, invalidate };
};

const OFFICE_MIME_TYPES = new Set([
  'application/msword',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  'application/vnd.ms-excel',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
]);

/**
 * Unified file preview renderer — supports PDF, images, video, audio, Word/Excel, and generic files.
 * Works for both the inline card and the fullscreen dialog.
 */
const FilePreviewRenderer = ({ file, fullscreen = false }) => {
  const theme = useTheme();
  const isMobileView = useMediaQuery(theme.breakpoints.down('sm'));
  const [imgZoomed, setImgZoomed] = useState(false);
  const mimeType = String(file?.signedFileType || file?.file_type || '').toLowerCase();
  const url = file?.signedUrl;
  const fileName = file?.file_name ?? file?.filename ?? 'document';

  if (!url) return null;

  // ── PDF ────────────────────────────────────────────────────────────────────
  if (mimeType === 'application/pdf' || mimeType === 'pdf') {
    const isElectron = window.electron;
    
    if (isElectron) {
      return (
        <Box sx={{ 
          height: fullscreen ? '100%' : '420px', 
          width: '100%', 
          display: 'flex', 
          flexDirection: 'column', 
          justifyContent: 'center', 
          alignItems: 'center', 
          p: 4, 
          textAlign: 'center',
          bgcolor: 'action.hover'
        }}>
          <Typography variant="h6" gutterBottom color="primary">
            PDF Document
          </Typography>
          <Typography variant="body2" color="textSecondary" sx={{ mb: 3 }}>
            PDF preview opens in a separate window in the Clinical Workstation for the best experience.
          </Typography>
          <Stack direction="row" spacing={2}>
            <Button 
              variant="contained" 
              onClick={() => window.open(url, '_blank', 'width=900,height=700,menubar=yes,toolbar=yes')}
              startIcon={<VisibilityIcon />}
            >
              Open PDF
            </Button>
            <Button 
              variant="outlined" 
              component="a"
              href={url}
              download={fileName}
              startIcon={<DownloadIcon />}
            >
              Download
            </Button>
          </Stack>
        </Box>
      );
    }

    const pdfSrc = `${url}#toolbar=1&view=FitH&navpanes=0&scrollbar=1`;
    const height = fullscreen ? '100%' : '420px';

    // Desktop browsers use native embed (best performance/toolbars)
    if (!isMobileView) {
      return (
        <Box sx={{ width: '100%', height, display: 'flex', flexDirection: 'column', bgcolor: 'grey.100' }}>
          <embed
            src={pdfSrc}
            type="application/pdf"
            style={{ width: '100%', flex: 1, border: 'none', display: 'block', minHeight: 0, height: '100%' }}
          />
        </Box>
      );
    }

    // Mobile browsers use DocViewer (avoids "Open" button failures)
    return (
      <Box sx={{ width: '100%', height, overflow: 'hidden' }}>
        <DocViewer
          key={url}
          documents={[{ uri: url, fileName, fileType: 'pdf' }]}
          pluginRenderers={DocViewerRenderers}
          config={{ 
            header: { 
              disableHeader: !fullscreen, // Show header with zoom tools in fullscreen mode
              disableFileName: true 
            } 
          }}
          style={{ height: '100%', width: '100%' }}
        />
      </Box>
    );
  }

  // ── Image ──────────────────────────────────────────────────────────────────
  if (mimeType.startsWith('image/')) {
    return (
      <Box
        onClick={() => setImgZoomed(z => !z)}
        sx={{
          width: '100%', height: '100%',
          display: 'flex', justifyContent: 'center', alignItems: 'center',
          cursor: imgZoomed ? 'zoom-out' : 'zoom-in',
          bgcolor: 'black',
          overflow: 'hidden',
        }}
      >
        <img
          src={url}
          alt={fileName}
          style={{
            maxWidth: imgZoomed ? 'none' : '100%',
            maxHeight: imgZoomed ? 'none' : '100%',
            width: imgZoomed ? 'auto' : undefined,
            height: imgZoomed ? 'auto' : undefined,
            objectFit: imgZoomed ? 'none' : 'contain',
            transition: 'transform 0.2s',
          }}
        />
      </Box>
    );
  }

  // ── Video ──────────────────────────────────────────────────────────────────
  if (mimeType.startsWith('video/')) {
    return (
      <Box sx={{ width: '100%', height: '100%', bgcolor: '#000', display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
        <video
          controls
          src={url}
          style={{ maxWidth: '100%', maxHeight: '100%', outline: 'none' }}
        >
          Your browser does not support the video element.
        </video>
      </Box>
    );
  }

  // ── Audio ──────────────────────────────────────────────────────────────────
  if (mimeType.startsWith('audio/')) {
    return (
      <Box sx={{ width: '100%', height: '100%', display: 'flex', justifyContent: 'center', alignItems: 'center', p: 3, bgcolor: 'action.hover' }}>
        <audio controls src={url} style={{ width: '100%', maxWidth: 500 }}>
          Your browser does not support the audio element.
        </audio>
      </Box>
    );
  }

  // ── Word / Excel ───────────────────────────────────────────────────────────
  if (OFFICE_MIME_TYPES.has(mimeType)) {
    const isWord = mimeType.includes('word');
    const isLocal = url.startsWith('http://localhost') || url.startsWith('blob:') || url.startsWith('/');
    const officeViewerUrl = `https://view.officeapps.live.com/op/embed.aspx?src=${encodeURIComponent(url)}`;
    
    // For public (non-local) URLs we can embed via Microsoft Office Online Viewer
    if (!isLocal) {
      return (
        <Box sx={{ width: '100%', height: fullscreen ? '100%' : 420, display: 'flex', flexDirection: 'column' }}>
          <iframe
            src={officeViewerUrl}
            title={`${isWord ? 'Word' : 'Excel'}: ${fileName}`}
            style={{ width: '100%', flex: 1, border: 'none', display: 'block', minHeight: 0 }}
            allow="fullscreen"
          />
        </Box>
      );
    }

    // For local files — show open button (Office Online can't reach localhost)
    return (
      <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', gap: 2, p: 4, textAlign: 'center' }}>
        <Box sx={{ fontSize: fullscreen ? 64 : 48, lineHeight: 1 }}>
          {isWord ? '📄' : '📊'}
        </Box>
        <Typography variant={fullscreen ? 'h5' : 'h6'} color="primary" fontWeight="bold">
          {isWord ? 'Word Document' : 'Excel Spreadsheet'}
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ maxWidth: 400 }}>
          {fileName}
        </Typography>
        <Stack direction="row" spacing={2} sx={{ mt: 1 }}>
          <Button
            variant="contained"
            startIcon={<FolderOpenIcon />}
            onClick={() => {
              const localPath = file?.storage_path || file?.file_path;
              if (localPath && window.electron) {
                window.electron.ipcRenderer.send('open-file', localPath);
              } else {
                window.open(url, '_blank');
              }
            }}
            sx={{ borderRadius: 2 }}
          >
            Open in {isWord ? 'Word' : 'Excel'}
          </Button>
          <Button
            variant="outlined"
            startIcon={<DownloadIcon />}
            component="a"
            href={url}
            download={fileName}
            sx={{ borderRadius: 2 }}
          >
            Download
          </Button>
        </Stack>
      </Box>
    );
  }

  // ── Generic fallback (DocViewer) ───────────────────────────────────────────
  return (
    <Box sx={{ width: '100%', height: '100%', overflow: 'hidden' }}>
      <DocViewer
        key={url}
        documents={[{ uri: url, fileName, fileType: file?.signedFileType }]}
        pluginRenderers={DocViewerRenderers}
        config={{ 
          header: { 
            disableHeader: !fullscreen, // Show header with zoom/nav tools in fullscreen mode
            disableFileName: true 
          } 
        }}
        style={{ height: '100%', width: '100%' }}
      />
    </Box>
  );
};

const handlePatientEditSubmit = async (apiClient, patientId, values, setEditSubmitting, setError, loadPatient, setEditDialogOpen) => {
  if (!apiClient || !patientId) {
    return;
  }

  setEditSubmitting(true);
  setError('');

  try {
    const payload = buildPatientUpdatePayload(values);

    await updatePatient(apiClient, patientId, payload);
    await loadPatient();
    setEditDialogOpen(false);
  } catch (err) {
    console.error('Failed to update patient:', err);
    const serverError = err.response?.data?.error ?? err.message ?? 'Failed to update patient.';
    setError(serverError);
  } finally {
    setEditSubmitting(false);
  }
};

const formatValue = (value) => {
  if (value === null || value === undefined) {
    return '—';
  }
  if (typeof value === 'string') {
    const trimmed = value.trim();
    return trimmed.length ? trimmed : '—';
  }
  return String(value);
};

const LabelBox = ({ label, children, color = '#d32f2f', sx = {} }) => (
  <Box sx={{ position: 'relative', mt: 1.5, mb: 1, display: 'inline-block', ...sx }}>
    <Typography
      variant="caption"
      sx={{
        position: 'absolute',
        top: -8,
        left: 8,
        bgcolor: 'background.paper',
        px: 0.5,
        color: color,
        fontSize: '0.65rem',
        fontWeight: 900,
        zIndex: 1,
        textTransform: 'uppercase',
        letterSpacing: 0.5,
        whiteSpace: 'nowrap'
      }}
    >
      {label}
    </Typography>
    <Box
      sx={{
        border: `1px solid ${color}`,
        borderRadius: 1.5,
        px: 1.2,
        py: 0.5,
        width: 'fit-content',
        minWidth: 60,
        display: 'flex',
        alignItems: 'center',
        bgcolor: color === '#d32f2f' ? 'rgba(211, 47, 47, 0.02)' : 'transparent'
      }}
    >
      {children}
    </Box>
  </Box>
);

const PatientDetailPage = () => {
  const { t } = useTranslation();
  const theme = useTheme();
  const isMobileView = useMediaQuery(theme.breakpoints.down('sm'));
  const { id } = useParams();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { isAuthenticated, user } = useAuth();
  const isAdmin = user?.role === 'admin';
  const canWrite = user?.role === 'admin' || user?.role === 'read-write';
  const { lastChange } = useSync();
  const { setCurrentPatient, clearPatientData, setActiveTab: setGlobalActiveTab } = usePatient();
  
  const activeTab = useMemo(() => {
    const tab = parseInt(searchParams.get('tab') || '0', 10);
    return isNaN(tab) ? 0 : tab;
  }, [searchParams]);

  const [patient, setPatient] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [patientsList, setPatientsList] = useState([]);
  const [currentPatientIndex, setCurrentPatientIndex] = useState(-1);
  const [goToPatientId, setGoToPatientId] = useState('');
  const [selectedPreviewId, setSelectedPreviewId] = useState(null);
  const [previewContent, setPreviewContent] = useState(null);
  const [previewError, setPreviewError] = useState('');
  const [isFullScreen, setIsFullScreen] = useState(false);
  const previewRef = useRef(null);
  const tabsSectionRef = useRef(null);
  const [folderCopyStatus, setFolderCopyStatus] = useState('');
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editSubmitting, setEditSubmitting] = useState(false);
  const [editError, setEditError] = useState('');
  const [editingField, setEditingField] = useState(null);
  const [fieldValue, setFieldValue] = useState('');
  const [isEditing, setIsEditing] = useState(false);
  const [editedPatient, setEditedPatient] = useState({});
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [fullReportDialogOpen, setFullReportDialogOpen] = useState(false);
  const [fullReportHtml, setFullReportHtml] = useState('');
  const [fullReportLoading, setFullReportLoading] = useState(false);
  const [createOpen, setCreateOpen] = useState(false);
  const [createSubmitting, setCreateSubmitting] = useState(false);
  const [createError, setCreateError] = useState('');
  const apiClient = useMemo(() => {
    if (!isAuthenticated) {
      return null;
    }
    const token = sessionStorage.getItem('token');
    if (!token) {
      return null;
    }
    return createApiClient(token);
  }, [isAuthenticated]);


  const [allFolders, setAllFolders] = useState([]);
  const [epembaseisOptions, setEpembaseisOptions] = useState([]);
  const [serverPathPc, setServerPathPc] = useState(
    () => typeof window !== 'undefined' ? localStorage.getItem('serverPathPc') || '' : ''
  );
  const [localPathPc, setLocalPathPc] = useState(
    () => typeof window !== 'undefined' ? localStorage.getItem('localPathPc') || '' : ''
  );

  useEffect(() => {
    const loadAllFolders = async () => {
      if (apiClient) {
        try {
          const [folders, epembaseis] = await Promise.all([
            fetchFolderSettings(apiClient),
            systemAPI.getEpembaseis()
          ]);
          setAllFolders(folders);
          setEpembaseisOptions(epembaseis.data.map(e => e.epembasi));
        } catch (err) {
          console.error('Failed to load initial settings:', err);
        }
      }
    };
    loadAllFolders();
  }, [apiClient]);

  useEffect(() => {
    const onStorage = (e) => {
      if (e.key === 'serverPathPc') setServerPathPc(e.newValue || '');
      if (e.key === 'localPathPc') setLocalPathPc(e.newValue || '');
    };
    window.addEventListener('storage', onStorage);
    return () => window.removeEventListener('storage', onStorage);
  }, []);

  const patientDbId = useMemo(() => resolvePatientId(patient), [patient]);
  const getPatientFolderPath = useCallback(() => {
    // 1. Check if patient has an explicit folder field (from some DB schemas)
    const explicitFolder = patient?.folder || patient?.pc_name || patient?.folder_name;
    if (explicitFolder) return explicitFolder;

    const firstName = patient?.firstName ?? patient?.first_name ?? '';
    const lastName = patient?.lastName ?? patient?.last_name ?? '';
    
    if (!firstName && !lastName) return '';
    
    // Standard patient naming: "LASTNAME FIRSTNAME"
    return `${lastName} ${firstName}`.trim();
  }, [patient]);

  const folderName = getPatientFolderPath();


  const getAbsolutePathForPc = useCallback((pcName) => {
    if (!pcName || !folderName || !allFolders.length) return '';
    const folder = allFolders.find(f => f.pc_name === pcName);
    if (!folder || !folder.folder) return '';
    
    const base = folder.folder;
    const separator = base.includes('\\') ? '\\' : '/';
    return base.endsWith(separator) 
      ? `${base}${folderName}` 
      : `${base}${separator}${folderName}`;
  }, [allFolders, folderName]);

  const serverAbsolutePath = useMemo(() => getAbsolutePathForPc(serverPathPc), [getAbsolutePathForPc, serverPathPc]);
  const localAbsolutePath = useMemo(() => getAbsolutePathForPc(localPathPc), [getAbsolutePathForPc, localPathPc]);

  const activePath = localAbsolutePath || serverAbsolutePath;
  const canOpenLocalFolder = Boolean(localAbsolutePath);

  useEffect(() => {
    setFolderCopyStatus('');
  }, [localAbsolutePath]);

  useEffect(() => {
    if (patient) {
      const name = `${patient.lastName ?? patient.last_name ?? ''} ${patient.firstName ?? patient.first_name ?? ''}`.trim();
      setCurrentPatient(patientDbId, name || 'Unknown Patient');
    }
    return () => {
      // Only clear if we are actually unmounting the detail page logic
      // Note: switching tabs doesn't unmount this component, but navigating away does.
    };
  }, [patient, patientDbId, setCurrentPatient]);

  useEffect(() => {
    setGlobalActiveTab(activeTab);
    
    // Auto-focus logic: scroll to tabs section when active tab changes
    // Delay slightly to ensure content is rendering and browser is ready
    const timer = setTimeout(() => {
      if (tabsSectionRef.current) {
        tabsSectionRef.current.scrollIntoView({ 
          behavior: 'smooth', 
          block: 'start'
        });
      }
    }, 100);
    
    return () => clearTimeout(timer);
  }, [activeTab, setGlobalActiveTab]);

  useEffect(() => {
    return () => clearPatientData();
  }, [clearPatientData]);

  useEffect(() => {
    if (!folderCopyStatus) {
      return undefined;
    }
    const timer = setTimeout(() => setFolderCopyStatus(''), 3000);
    return () => clearTimeout(timer);
  }, [folderCopyStatus]);

  const handleOpenFolder = useCallback(async () => {
    if (!canOpenLocalFolder) return;
    
    try {
      const success = await openFolder(localAbsolutePath);
      if (!success) {
        throw new Error('Failed to open folder');
      }
    } catch (error) {
      console.error('Error opening folder:', error);
      alert(`Could not open folder. Please check if the path is correct and accessible.\n\nPath: ${localAbsolutePath}\n\nError: ${error.message}`);
    }
  }, [canOpenLocalFolder, localAbsolutePath]);
  
  const handleOpenInBrowser = useCallback(() => {
    window.open(`/patients/${id}?tab=5&minimal=true`, '_blank');
  }, [id]);

  const handleCopyPath = useCallback(async (path) => {
    if (!path) return;
    try {
      const success = await copyToClipboard(path);
      if (success) {
        setFolderCopyStatus('Path copied to clipboard!');
        setTimeout(() => setFolderCopyStatus(''), 3000);
      } else {
        throw new Error('Failed to copy to clipboard');
      }
    } catch (err) {
      console.error('Copy error:', err);
      alert('Failed to copy path: ' + err.message);
    }
  }, []);

  const handleCopyFolder = useCallback(() => handleCopyPath(localAbsolutePath), [handleCopyPath, localAbsolutePath]);

  const handleOpenFileFullscreen = useCallback(() => {
    if (!previewContent?.signedUrl) return;

    if (isMobileView) {
      setIsFullScreen(true);
    } else {
      // On desktop, open in a new tab/window for free movement
      const win = window.open(previewContent.signedUrl, '_blank', 'width=1000,height=800,menubar=no,status=no,toolbar=no');
      if (!win) {
        // Fallback to internal dialog if browser blocked the popup
        setIsFullScreen(true);
      }
    }
  }, [previewContent, isMobileView]);

  const loadPatient = useCallback(async () => {
    if (!apiClient || !id) {
      setLoading(false);
      setError('You must be signed in to view patient details.');
      return;
    }

    setLoading(true);
    setError('');

    const attemptFetch = async (client) => {
      const pageSize = 100;
      const aggregated = [];
      let currentPage = 1;
      let totalPages = 1;

      // Fetch all pages to ensure we find the patient
      do {
        console.log(`[PatientDetailPage] Fetching page ${currentPage} of patients...`);
        const result = await fetchPatients(client, currentPage, pageSize);
        console.log(`[PatientDetailPage] Page ${currentPage} result:`, result);
        const pagePatients = Array.isArray(result.patients) ? result.patients : [];
        console.log(`[PatientDetailPage] Page ${currentPage} has ${pagePatients.length} patients`);
        aggregated.push(...pagePatients);

        if (result.pagination?.totalPages) {
          totalPages = result.pagination.totalPages;
        } else if (pagePatients.length < pageSize) {
          totalPages = currentPage;
        }

        currentPage += 1;
      } while (currentPage <= totalPages);
      
      console.log(`[PatientDetailPage] Total aggregated patients: ${aggregated.length}`);

      const normalized = aggregated
        .map((item, index) => normalizePatient(item, index))
        .sort((a, b) => {
          // Sort by ID in ascending order
          const idA = a.id || a.ID_PT || '';
          const idB = b.id || b.ID_PT || '';
          return idA.localeCompare(idB, undefined, { numeric: true });
        });

      setPatientsList(normalized);
      const found = normalized.find(
        (p) => String(p.id ?? p.__recordId ?? p.ID_PT ?? '') === String(id)
      );

      if (!found) {
        throw new Error('Patient not found');
      }

      const foundIndex = normalized.findIndex(
        (p) => String(p.id ?? p.__recordId ?? p.ID_PT ?? '') === String(id)
      );

      console.log('[PatientDetailPage] Found patient:', {
        urlId: id,
        patientId: found.id,
        recordId: found.__recordId,
        originalId: found.__originalId,
        ID_PT: found.ID_PT,
        fullPatient: found
      });

      setPatient(found);
      setCurrentPatientIndex(foundIndex);
    };

    try {
      await attemptFetch(apiClient);
    } catch (err) {
      if (err.response?.status === 401) {
        setError('Session expired. Please sign in again.');
      } else {
        setError(err.response?.data?.error ?? err.message ?? 'Failed to load patient');
      }
    } finally {
      setLoading(false);
    }
  }, [apiClient, id]);

  useEffect(() => {
    loadPatient();
  }, [loadPatient]);

  const { files: patientFiles, loading: filesLoading, error: filesError, reload: reloadFiles } =
    usePatientFiles(apiClient, patientDbId ?? id ?? null);

  const localApiClient = useMemo(() => {
    // Dynamically resolve the clinical server address
    // In multi-device offices, 'localhost' only works on the server machine itself.
    // We use the same host the app is currently loaded from.
    const baseUrl = apiClient?.defaults?.baseURL || `${window.location.origin}/api`;
    const instance = axios.create({
      baseURL: baseUrl
    });
    // Clinical Local Client: We bypass remote authentication headers for same-machine access if token is missing
    // or include it if available (hybrid auth)
    instance.interceptors.request.use(config => {
      const token = sessionStorage.getItem('token');
      if (token) config.headers.Authorization = `Bearer ${token}`;
      return config;
    });
    return instance;
  }, [apiClient]);

  const { fetchSignedUrl: fetchPreviewSignedUrl, invalidate: invalidatePreviewCache } =
    usePatientFilePreview(apiClient, localApiClient, patientFiles);

  // Real-time surgical synchronization: Auto-refresh when relevant data changes on other workstations
  useEffect(() => {
    if (!lastChange) return;
    
    const { table, id, patientId, action } = lastChange;
    const currentId = parseInt(patientDbId || id);
    
    const isRelevantPatient = (table === 'patients' && parseInt(id) === currentId);
    const isRelevantRelatedData = (['visits', 'files', 'medhistory', 'medications', 'operations'].includes(table) && 
                                   (parseInt(patientId) === currentId || parseInt(id) === currentId));

    if (isRelevantPatient || isRelevantRelatedData) {
      console.log(`[Sync] Auto-refreshing clinical data for patient ${currentId} due to ${action} in ${table}`);
      loadPatient();
      // Also reload files if the change was in files table
      if (table === 'files') {
        reloadFiles();
      }
    }
  }, [lastChange, patientDbId, loadPatient, reloadFiles]);

  const handleEditSubmit = useCallback(
    async (values) => {
      await handlePatientEditSubmit(
        apiClient,
        patientDbId,
        values,
        setEditSubmitting,
        setEditError,
        loadPatient,
        setEditDialogOpen
      );
    },
    [apiClient, patientDbId, loadPatient]
  );

  const handleCreateOpen = useCallback(() => {
    setCreateError('');
    setCreateOpen(true);
  }, []);

  const handleCreateClose = useCallback(() => {
    setCreateOpen(false);
  }, []);

  const handleCreateSubmit = useCallback(async (values, formikHelpers) => {
    if (!apiClient) {
      formikHelpers.setStatus(t('You must be signed in to add patient records.'));
      formikHelpers.setSubmitting(false);
      return;
    }

    const payload = buildPatientUpdatePayload(values);

    setCreateSubmitting(true);
    setCreateError('');
    formikHelpers.setStatus(undefined);

    try {
      const created = await createPatient(apiClient, payload);
      setCreateOpen(false);
      formikHelpers.resetForm();
      
      // Navigate to the new patient's detail page
      // Use the ID from the response - try both standard and medical specific fields
      const newId = created.id ?? created.ID_PT ?? created.__recordId;
      if (newId) {
        navigate(`/patients/${newId}`);
      } else {
        navigate('/patients');
      }
    } catch (err) {
      console.error('Failed to create patient:', err);
      const message = err.response?.data?.error ?? err.message ?? t('Failed to add patient');
      setCreateError(message);
      formikHelpers.setStatus(message);
    } finally {
      setCreateSubmitting(false);
      formikHelpers.setSubmitting(false);
    }
  }, [apiClient, navigate, t]);

  const handleStartEditing = (field, value) => {
    setEditingField(field);
    setFieldValue(value || '');
  };

  const handleSaveField = async () => {
    if (!editingField) return;
    
    try {
      setEditSubmitting(true);
      setEditError('');
      
      // Map field names to their corresponding backend field names
      const fieldMap = {
        age: 'age',
        lastVisit: 'last_visit',
        cardDate: 'card_date',
        dateOfBirth: 'date_of_birth',
        // Add other fields as needed
      };
      
      const backendField = fieldMap[editingField] || editingField;
      let valueToSend = fieldValue;
      
      // Handle date fields consistently with date of birth
      if (['card_date', 'date_of_birth'].includes(backendField)) {
        // For date fields, use the value as-is (it's already in YYYY-MM-DD format from the date input)
        // The server will handle the date conversion
        valueToSend = valueToSend || null;
      } else if (backendField === 'last_visit') {
        // For last_visit, only update if it's explicitly set by the user
        // This prevents automatic updates from the visits tab
        if (!valueToSend || valueToSend === patient.last_visit) {
          setEditingField(null);
          setFieldValue('');
          return;
        }
      }
      
      const payload = { [backendField]: valueToSend, manual_last_visit_update: true };
      
      await handlePatientEditSubmit(
        apiClient,
        patientDbId,
        payload,
        () => {},
        setEditError,
        loadPatient,
        () => {}
      );
      
      setEditingField(null);
      setFieldValue('');
    } catch (err) {
      console.error('Error updating field:', err);
      setEditError('Failed to update field. Please try again.');
    } finally {
      setEditSubmitting(false);
    }
  };
  
  const handleKeyDown = (e) => {
    if (e.key === 'Enter') {
      handleSaveField();
    } else if (e.key === 'Escape') {
      setEditingField(null);
      setFieldValue('');
    }
  };

  useEffect(() => {
    if (!selectedPreviewId && patientFiles.length > 0) {
      const historyFile = patientFiles.find(
        (f) => String(f.description || '').trim().toUpperCase() === 'ΙΣΤΟΡΙΚΟ'
      );
      if (historyFile) {
        setSelectedPreviewId(historyFile.id_file ?? historyFile.id);
      }
    }
  }, [patientFiles, selectedPreviewId]);

  useEffect(() => {
    if (!patientFiles.length) {
      if (selectedPreviewId) setSelectedPreviewId(null);
      if (previewContent) setPreviewContent(null);
      return;
    }

    if (selectedPreviewId) {
      const match = patientFiles.find((file) => String(file.id_file ?? file.id) === String(selectedPreviewId));
      if (!match) {
        setSelectedPreviewId(null);
        setPreviewContent(null);
        setPreviewError('');
        return;
      }

      // If we have an ID but no content (e.g. after refresh), auto-fetch it
      if (!previewContent) {
        const fetchPreview = async () => {
          try {
            const signed = await fetchPreviewSignedUrl(selectedPreviewId);
            if (signed?.url) {
              setPreviewContent({
                ...match,
                signedUrl: signed.url,
                signedFileType: signed.fileType ?? match.file_type,
              });
            }
          } catch (err) {
            console.error('Auto-preview fetch failed:', err);
          }
        };
        fetchPreview();
      }
    }
  }, [patientFiles, selectedPreviewId, previewContent, fetchPreviewSignedUrl]);

  const handleBack = () => {
    navigate('/patients');
  };

  const handleDeleteClick = () => {
    setDeleteConfirmOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!apiClient || !patientDbId) {
      setError('Cannot delete patient: Missing required data');
      setDeleteConfirmOpen(false);
      return;
    }

    try {
      // Use the deletePatient function from patientService
      await deletePatient(apiClient, patientDbId);

      // Navigate back to patients list after successful deletion
      navigate('/patients', {
        state: {
          message: `Patient ${patient.lastName} ${patient.firstName} deleted successfully`,
          severity: 'success',
        },
      });
    } catch (err) {
      if (err.response?.status === 401) {
        setError('Session expired. Please sign in again.');
      } else {
        setError(err.response?.data?.error ?? err.message ?? 'Failed to delete patient');
      }
    } finally {
      setDeleteConfirmOpen(false);
    }
  };

  const handleDeleteCancel = () => {
    setDeleteConfirmOpen(false);
  };

  const handlePrintPatient = useCallback(async () => {
    if (!patient || !apiClient) return;

    setFullReportLoading(true);
    setError('');

    const isHistory = (file) => String(file.description || '').trim().toUpperCase() === 'ΙΣΤΟΡΙΚΟ';
    
    const renderPdfFirstPage = async (url) => {
      const pdfjs = window.pdfjsLib;
      if (!pdfjs || !url) return null;
      try {
        const loadingTask = pdfjs.getDocument({ url, withCredentials: false });
        const pdf = await loadingTask.promise;
        const page = await pdf.getPage(1);
        // Using a higher scale (1.5) for better print quality in the report
        const viewport = page.getViewport({ scale: 1.5 });
        const canvas = document.createElement('canvas');
        canvas.height = viewport.height;
        canvas.width = viewport.width;
        const context = canvas.getContext('2d');
        await page.render({ canvasContext: context, viewport }).promise;
        const dataUrl = canvas.toDataURL('image/jpeg', 0.8);
        pdf.destroy();
        return dataUrl;
      } catch (err) {
        console.error('[Report] PDF rendering failed:', err);
        return null;
      }
    };

    try {
      const patientId = resolvePatientId(patient);
      
      // Fetch all clinical data in parallel for a comprehensive report
      const [
        historyRes,
        medsRes,
        opsRes,
        visitsRes,
        filesRes
      ] = await Promise.all([
        medHistoryAPI.getByPatientId(patientId).catch(() => ({ data: [] })),
        medicationAPI.getByPatientId(patientId).catch(() => ({ data: [] })),
        operationAPI.getByPatientId(patientId).catch(() => ({ data: [] })),
        fetchVisits(apiClient, patientId).catch(() => ([])),
        patientFileAPI.getByPatientId(patientId).catch(() => ({ data: [] }))
      ]);

      const rawFiles = filesRes.data || [];
      
      // Pre-fetch signed URLs for files with description 'ΙΣΤΟΡΙΚΟ' to embed them in the report
      const enrichedFiles = await Promise.all(
        rawFiles.map(async (file) => {
          if (isHistory(file)) {
            try {
              const signed = await fetchPreviewSignedUrl(file.id_file ?? file.id);
              const url = signed?.url;
              if (!url) return file;

              const isPdf = String(file.file_type || '').toLowerCase() === 'application/pdf' || 
                           String(file.file_name || '').toLowerCase().endsWith('.pdf');
              
              let renderedThumb = null;
              if (isPdf) {
                renderedThumb = await renderPdfFirstPage(url);
              }

              return { ...file, signedUrl: url, renderedThumb };
            } catch (err) {
              console.warn(`Failed to fetch signed URL or render PDF for report file ${file.id}:`, err);
              return file;
            }
          }
          return file;
        })
      );

      const reportData = {
        patient,
        history: historyRes.data || [],
        medications: medsRes.data || [],
        operations: opsRes.data || [],
        visits: visitsRes || [],
        files: enrichedFiles
      };

      const html = generateFullClinicalReport(reportData);
      setFullReportHtml(html);
      setFullReportDialogOpen(true);
    } catch (err) {
      console.error('Failed to generate full report:', err);
      setError('An error occurred while generating the clinical report.');
    } finally {
      setFullReportLoading(false);
    }
  }, [patient, apiClient]);

  // Using existing folder handling implementation from earlier in the file

  const resolvePatientRouteId = (patientRecord) =>
    patientRecord?.id ?? patientRecord?.__recordId ?? patientRecord?.ID_PT ?? null;

  const handleNavigateToPatient = (targetIndex) => {
    if (targetIndex < 0 || targetIndex >= patientsList.length) {
      return;
    }
    const targetPatient = patientsList[targetIndex];
    const targetId = resolvePatientRouteId(targetPatient);
    if (targetId) {
      navigate(`/patients/${targetId}`);
    }
  };

  const handlePreviousPatient = () => {
    handleNavigateToPatient(currentPatientIndex - 1);
  };

  const handleNextPatient = () => {
    handleNavigateToPatient(currentPatientIndex + 1);
  };

  const handleFirstPatient = () => {
    handleNavigateToPatient(0);
  };

  const handleLastPatient = () => {
    handleNavigateToPatient(patientsList.length - 1);
  };

  const handleGoToPatient = () => {
    const trimmedId = goToPatientId.trim();
    if (!trimmedId) return;
    
    navigate(`/patients/${trimmedId}`);
    setGoToPatientId('');
  };

  const handleGoToPatientKeyPress = (event) => {
    if (event.key === 'Enter') {
      handleGoToPatient();
    }
  };

  const handleRefresh = () => {
    if (window.electron) {
      window.electron.ipcRenderer.send('reload-window');
    } else {
      window.location.reload();
    }
  };

  const handleTabChange = (event, newValue) => {
    setSearchParams({ tab: newValue.toString() });
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="60vh">
        <CircularProgress />
      </Box>
    );
  }

  if (error || !patient) {
    return (
      <Box>
        <Button startIcon={<ArrowBackIcon />} onClick={handleBack} sx={{ mb: 2 }}>
          Back to Patients
        </Button>
        <Alert severity="error">{error || 'Patient not found'}</Alert>
      </Box>
    );
  }

  const firstName = patient?.firstName ?? patient?.first_name ?? '';
  const lastName = patient?.lastName ?? patient?.last_name ?? '';
  const displayName = formatValue(
    patient.__displayName ??
      `${lastName} ${firstName}`.trim()
  );
  const age = formatValue(patient.__age ?? patient.age);
  const gender = formatValue(patient.__gender ?? patient.gender);
  const phone = formatValue(patient.__phone ?? patient.phone);
  const email = formatValue(patient.__email ?? patient.email);
  const dateOfBirth = formatDate(patient.__dateOfBirth ?? patient.dateOfBirth ?? patient.date_birth);
  const address = formatValue(patient.__address ?? patient.address);
  const notes = formatValue(patient.__notes ?? patient.notes);
  const diagnosis = formatValue(patient.__diagnosis ?? patient.diagnosis);
  const amka = formatValue(patient.__amka ?? patient.amka);
  const am = formatValue(patient.__am ?? patient.am);
  const death = formatValue(patient.__death ?? patient.death);
  const lastVisit = formatDate(patient.__lastVisit ?? patient.last_visit);
  const cardDate = formatDate(patient.__cardDate ?? patient.card_date ?? patient.datekarta ?? patient.__createdAt ?? patient.created_at);
  const updatedAt = formatDate(patient.__updatedAt ?? patient.updated_at);
  const allergies = formatValue(patient.allergies);
  const detailEntries = Array.isArray(patient.__detailEntries)
    ? patient.__detailEntries.filter(({ key }) =>
        ![
          'first_name',
          'last_name',
          '__dateOfBirth',
          '__age',
          '__gender',
          '__phone',
          '__email',
          '__address',
          '__diagnosis',
          '__notes',
        ].includes(key)
      )
    : [];
  const patientRecordId = formatValue(patientDbId ?? patient.id ?? patient.__recordId ?? patient.ID_PT);
  const hasPreviousPatient = currentPatientIndex > 0;
  const hasNextPatient = currentPatientIndex >= 0 && currentPatientIndex < patientsList.length - 1;
  
  console.log('[PatientDetailPage] Patient DB ID for tabs:', {
    patientDbId,
    __recordId: patient.__recordId,
    __originalId: patient.__originalId,
    id: patient.id
  });
  
  if (!patientDbId) {
    console.error('[PatientDetailPage] WARNING: patientDbId is undefined! Patient object:', patient);
  }

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '80vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ p: 2 }}>
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
        <Button startIcon={<ArrowBackIcon />} onClick={handleBack}>
          Back to Patients
        </Button>
      </Box>
    );
  }

  if (!patient) {
    return (
      <Box sx={{ p: 2 }}>
        <Alert severity="warning" sx={{ mb: 2 }}>
          Patient not found
        </Alert>
        <Button startIcon={<ArrowBackIcon />} onClick={handleBack}>
          Back to Patients
        </Button>
      </Box>
    );
  }

  if (searchParams.get('minimal') === 'true') {
    return (
      <Box sx={{ p: { xs: 2, md: 4 }, bgcolor: 'background.default', minHeight: '100vh' }}>
        <Typography 
          variant="h4" 
          sx={{ 
            mb: 2, 
            fontWeight: 700, 
            textAlign: 'center',
            fontSize: { xs: '1.5rem', md: '2.125rem' }
          }}
        >
          {displayName}
        </Typography>
        <Typography 
          variant="h6" 
          color="text.secondary" 
          sx={{ mb: 4, textAlign: 'center', fontWeight: 500 }}
        >
          AMKA: {amka}
        </Typography>
        <Divider sx={{ mb: 4 }} />
        <PatientGalleryTab 
          patientId={patientDbId}
          apiClient={apiClient}
          folderPath={serverAbsolutePath}
        />
      </Box>
    );
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '80vh', p: 2, gap: 2 }}>
      <Stack 
        direction="column"
        spacing={2} 
        sx={{ 
          flexShrink: 0, 
          borderBottom: '1px solid', 
          borderColor: 'divider', 
          pb: 2,
        }}
      >
        {/* Top Row: Back button and Actions */}
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%' }}>
          <Button 
            startIcon={<ArrowBackIcon />} 
            onClick={handleBack} 
            sx={{ minWidth: { xs: 40, sm: 'auto' } }}
          >
            <Box component="span" sx={{ display: { xs: 'none', sm: 'inline' } }}>
              {t('Back to Patients')}
            </Box>
          </Button>

          <Stack direction="row" spacing={1} alignItems="center">
            {/* Desktop Actions */}
            <Box sx={{ display: { xs: 'none', sm: 'flex' }, gap: 1 }}>
              {canWrite && (
                <Button 
                  startIcon={<AddIcon />} 
                  onClick={handleCreateOpen} 
                  variant="contained" 
                  color="primary"
                  size="small"
                >
                  {t('Add New')}
                </Button>
              )}
              <Button 
                startIcon={<EditIcon />} 
                onClick={() => setEditDialogOpen(true)} 
                variant="outlined" 
                color="primary"
                size="small"
              >
                {t('Edit')}
              </Button>
              <Button 
                startIcon={fullReportLoading ? <CircularProgress size={16} color="inherit" /> : <PrintIcon />} 
                onClick={handlePrintPatient} 
                variant="outlined" 
                color="primary"
                disabled={fullReportLoading}
                size="small"
              >
                {fullReportLoading ? t('Preparing...') : t('Print')}
              </Button>
              <Button 
                startIcon={<DeleteIcon />} 
                onClick={handleDeleteClick} 
                variant="outlined" 
                color="error"
                size="small"
              >
                {t('Delete')}
              </Button>
            </Box>

            {/* Mobile Actions (Icons only) */}
            <Box sx={{ display: { xs: 'flex', sm: 'none' }, gap: 0.5 }}>
              {canWrite && (
                <IconButton onClick={handleCreateOpen} color="primary" size="small" sx={{ border: '1px solid', borderColor: 'primary.light' }}>
                  <AddIcon fontSize="small" />
                </IconButton>
              )}
              <IconButton onClick={() => setEditDialogOpen(true)} color="primary" size="small" sx={{ border: '1px solid', borderColor: 'primary.light' }}>
                <EditIcon fontSize="small" />
              </IconButton>
              <IconButton onClick={handlePrintPatient} color="primary" size="small" sx={{ border: '1px solid', borderColor: 'primary.light' }} disabled={fullReportLoading}>
                {fullReportLoading ? <CircularProgress size={16} /> : <PrintIcon fontSize="small" />}
              </IconButton>
              <IconButton onClick={handleDeleteClick} color="error" size="small" sx={{ border: '1px solid', borderColor: 'error.light' }}>
                <DeleteIcon fontSize="small" />
              </IconButton>
            </Box>
          </Stack>
        </Box>

        {/* Bottom Row: Pagination */}
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.5, flexWrap: 'wrap' }}>
          <Tooltip title={t('First patient')}>
            <IconButton onClick={handleFirstPatient} disabled={currentPatientIndex === 0} size="small">
              <FirstPageIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title={t('Previous patient')}>
            <IconButton onClick={handlePreviousPatient} disabled={currentPatientIndex === 0} size="small">
              <ChevronLeftIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          
          <Autocomplete
            size="small"
            options={patientsList}
            getOptionLabel={(option) => {
              if (typeof option === 'string') return option;
              const name = option.__displayName || `${option.lastName || ''} ${option.firstName || ''}`.trim();
              const amka = option.amka || option.__amka || '';
              const id = option.id || option.__recordId || option.ID_PT || '';
              return `${name} ${amka ? `(${amka})` : ''} [${id}]`.trim();
            }}
            filterOptions={(options, { inputValue }) => {
              const term = normalizeGreekSearch(inputValue);
              return options.filter(option => {
                const name = normalizeGreekSearch(option.__displayName || '');
                const lastName = normalizeGreekSearch(option.lastName || '');
                const firstName = normalizeGreekSearch(option.firstName || '');
                const amka = normalizeGreekSearch(option.amka || option.__amka || '');
                const id = String(option.id || option.__recordId || option.ID_PT || '').toLowerCase();
                const phone = normalizeGreekSearch(option.phone || option.__phone || '');
                
                return name.includes(term) || 
                       lastName.includes(term) || 
                       firstName.includes(term) || 
                       amka.includes(term) || 
                       id.includes(term) ||
                       phone.includes(term);
              });
            }}
            onChange={(event, newValue) => {
              if (newValue) {
                const targetId = resolvePatientRouteId(newValue);
                if (targetId) {
                  navigate(`/patients/${targetId}`);
                }
              }
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                placeholder={t('Search Patients...')}
                variant="outlined"
                sx={{ width: { xs: 150, sm: 250, md: 300 }, mx: 0.5 }}
                InputProps={{
                  ...params.InputProps,
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon fontSize="small" />
                    </InputAdornment>
                  ),
                }}
              />
            )}
            noOptionsText={t('No patients found')}
          />

          <Tooltip title={t('Next patient')}>
            <IconButton onClick={handleNextPatient} disabled={currentPatientIndex >= patientsList.length - 1} size="small">
              <ChevronRightIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title={t('Last patient')}>
            <IconButton onClick={handleLastPatient} disabled={currentPatientIndex >= patientsList.length - 1} size="small">
              <LastPageIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title={t('Refresh')}>
            <IconButton onClick={handleRefresh} size="small">
              <RefreshIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>
      </Stack>

      <Box sx={{ flex: 1, overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: 2 }}>
        <Card sx={{ flexShrink: 0 }}>
        <CardContent>
          <Grid container spacing={3} alignItems="flex-start">
            <Grid item xs={12} md={6} xl={7}>
              <Stack direction="row" spacing={2} alignItems="flex-start">
                <Avatar sx={{ width: { xs: 44, sm: 80 }, height: { xs: 44, sm: 80 }, bgcolor: 'primary.main' }}>
                  <PersonIcon sx={{ fontSize: { xs: 28, sm: 48 } }} />
                </Avatar>

                <Box flex={1}>
                  <Stack direction="row" justifyContent="space-between" alignItems="flex-start" spacing={1.5}>
                    <Box>
                      <Stack direction="row" spacing={1.5} alignItems="center" mb={1}>
                        <Typography 
                          variant="h4" 
                          sx={{ 
                            fontSize: { xs: '1.25rem', sm: '2rem', md: '2.125rem' },
                            fontWeight: 800,
                            wordBreak: 'break-word',
                            lineHeight: 1.2,
                            display: 'inline-block'
                          }}
                        >
                          {displayName}
                        </Typography>
                        {patientRecordId !== '—' && (
                          <Typography variant="subtitle2" color="text.secondary" sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>
                            ID: {patientRecordId}
                          </Typography>
                        )}
                      </Stack>
                      <Stack spacing={2} sx={{ mb: 1 }}>
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, alignItems: 'center' }}>
                          {amka !== '—' && (
                            <LabelBox label={t('AMKA')}>
                               <Typography variant="body2" color="text.primary">{amka}</Typography>
                            </LabelBox>
                          )}
                          {age !== '—' && (
                            <LabelBox label={t('Age')}>
                               <Typography variant="body2" color="text.primary">{age}</Typography>
                            </LabelBox>
                          )}
                          {dateOfBirth !== '—' && (
                            <LabelBox label={t('DOB')} color="black">
                              <Typography variant="body2" color="text.primary">{dateOfBirth}</Typography>
                            </LabelBox>
                          )}
                          {gender !== '—' && (
                            <LabelBox label={t('Gender')} color="black" sx={{ minWidth: 40 }}>
                              <Typography variant="body2" color="text.primary">{gender}</Typography>
                            </LabelBox>
                          )}
                          {cardDate && (
                            <LabelBox label={t('Card Date')}>
                               <Typography variant="body2" color="text.primary">{cardDate}</Typography>
                            </LabelBox>
                          )}
                          {phone !== '—' && (
                            <LabelBox label={t('Phone')}>
                               <Typography variant="body2" color="text.primary">{phone}</Typography>
                            </LabelBox>
                          )}
                        </Box>

                        {diagnosis !== '—' && (
                          <LabelBox label={t('Diagnosis')} sx={{ width: '100%', mt: 0.5 }}>
                             <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                               {diagnosis.split(', ').map((d, idx) => (
                                 <Chip 
                                   key={idx} 
                                   label={d} 
                                   size="small" 
                                   color="primary" 
                                   variant="outlined" 
                                   clickable
                                   onClick={() => window.open(`/query?diagnosis=${encodeURIComponent(d)}`, '_blank')}
                                   sx={{ fontWeight: 600, height: 24 }}
                                 />
                               ))}
                             </Box>
                          </LabelBox>
                        )}
                      </Stack>
                      {serverAbsolutePath && (
                        <Stack direction="row" spacing={1} alignItems="center" mt={0.5} flexWrap="wrap">
                          <Typography variant="body2" color="text.secondary">
                            {t('Server path')}:
                          </Typography>
                          <Typography
                            variant="body2"
                            sx={{ fontFamily: 'monospace', wordBreak: 'break-word' }}
                          >
                            {serverAbsolutePath}
                          </Typography>
                          <Stack direction="row" spacing={1}>
                            <Button size="small" variant="outlined" onClick={handleOpenInBrowser}>
                              {t('Open gallery')}
                            </Button>
                            <Button size="small" variant="text" onClick={() => handleCopyPath(serverAbsolutePath)}>
                              {t('Copy')}
                            </Button>
                          </Stack>
                        </Stack>
                      )}
                      {localAbsolutePath && (
                        <Stack direction="row" spacing={1} alignItems="center" mt={0.5} flexWrap="wrap">
                          <Typography variant="body2" color="text.secondary">
                            {t('Local path')}:
                          </Typography>
                          <Typography
                            variant="body2"
                            sx={{ fontFamily: 'monospace', wordBreak: 'break-word' }}
                          >
                            {localAbsolutePath}
                          </Typography>
                          <Stack direction="row" spacing={1}>
                            <Button size="small" variant="outlined" onClick={handleOpenFolder}>
                              {t('Open Folder')}
                            </Button>
                            {!serverAbsolutePath && (
                              <Button size="small" variant="outlined" onClick={handleOpenInBrowser}>
                                {t('Open gallery')}
                              </Button>
                            )}
                            <Button size="small" variant="text" onClick={handleCopyFolder}>
                              {t('Copy')}
                            </Button>
                          </Stack>
                        </Stack>
                      )}
                      {!serverAbsolutePath && !localAbsolutePath && (
                        <Typography variant="body2" color="text.disabled" mt={1.5}>
                          {t('Folder:')} {t('Not available')}
                        </Typography>
                      )}
                      {folderCopyStatus && (
                        <Typography variant="caption" color="text.secondary" mt={0.5} sx={{ display: 'block' }}>
                          {folderCopyStatus}
                        </Typography>
                      )}
                    </Box>

                    <Stack direction="row" spacing={1}>
                      {isEditing && (
                        <>
                          <Button
                            size="small"
                            variant="contained"
                            color="primary"
                            onClick={async () => {
                              try {
                                setEditSubmitting(true);
                                setEditError('');

                                const payload = buildPatientUpdatePayload(editedPatient);

                                await updatePatient(apiClient, patientDbId, payload);
                                await loadPatient();
                                setIsEditing(false);
                                setEditedPatient({});
                              } catch (err) {
                                console.error('Update error:', err);
                                setEditError(err.message ?? 'Failed to update patient');
                              } finally {
                                setEditSubmitting(false);
                              }
                            }}
                            disabled={editSubmitting}
                          >
                            {t('Save')}
                          </Button>
                          <Button
                            size="small"
                            variant="outlined"
                            onClick={() => {
                              setIsEditing(false);
                              setEditedPatient({});
                              setEditError('');
                            }}
                            disabled={editSubmitting}
                          >
                            {t('Cancel')}
                          </Button>
                        </>
                      )}
                    </Stack>
                  </Stack>

                  <Divider sx={{ my: { xs: 1, sm: 2 } }} />

                  <Box>
                    <Stack direction="row" justifyContent="space-between" alignItems="center" mb={1.5}>
                      <Typography variant="subtitle2" color="text.secondary">
                        {t('Patient Information')}
                      </Typography>
                      {editError && (
                        <Alert severity="error" sx={{ py: 0.5 }}>{editError}</Alert>
                      )}
                    </Stack>
                    <Grid container spacing={1.5}>
                      {/* Row 1 */}
                      <Grid item xs={12} md={4}>
                        <Box>
                          <Typography variant="caption" color="text.secondary" display="block">
                            {t('Last Name')}
                          </Typography>
                          {isEditing ? (
                            <TextField
                              fullWidth
                              size="small"
                              margin="dense"
                              value={editedPatient.lastName ?? ''}
                              onChange={(e) => setEditedPatient({ ...editedPatient, lastName: e.target.value })}
                            />
                          ) : (
                            <Typography variant="body2" sx={{ minHeight: 36, display: 'flex', alignItems: 'center', wordBreak: 'break-word', fontSize: { xs: '0.8rem', sm: '0.875rem' } }}>
                              {lastName || '—'}
                            </Typography>
                          )}
                        </Box>
                      </Grid>
                      <Grid item xs={12} md={4}>
                        <Box>
                          <Typography variant="caption" color="text.secondary" display="block">
                            {t('First Name')}
                          </Typography>
                          {isEditing ? (
                            <TextField
                              fullWidth
                              size="small"
                              margin="dense"
                              value={editedPatient.firstName ?? ''}
                              onChange={(e) => setEditedPatient({ ...editedPatient, firstName: e.target.value })}
                            />
                          ) : (
                            <Typography variant="body2" sx={{ minHeight: 36, display: 'flex', alignItems: 'center' }}>
                              {firstName || '—'}
                            </Typography>
                          )}
                        </Box>
                      </Grid>
                      <Grid item xs={12} md={4}>
                        <Box>
                          <Typography variant="caption" color="text.secondary" display="block">
                            {t('AMKA')}
                          </Typography>
                          {isEditing ? (
                            <TextField
                              fullWidth
                              size="small"
                              margin="dense"
                              value={editedPatient.amka ?? ''}
                              onChange={(e) => setEditedPatient({ ...editedPatient, amka: e.target.value })}
                            />
                          ) : (
                            <Typography variant="body2" sx={{ minHeight: 36, display: 'flex', alignItems: 'center' }}>
                              {amka || '—'}
                            </Typography>
                          )}
                        </Box>
                      </Grid>

                      {/* Row 2 */}
                      <Grid item xs={12} md={4}>
                        <Box>
                          <Typography variant="caption" color="text.secondary" display="block">
                            {t('Age')}
                          </Typography>
                          {editingField === 'age' ? (
                            <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                              <TextField
                                type="number"
                                size="small"
                                fullWidth
                                margin="dense"
                                value={fieldValue}
                                onChange={(e) => setFieldValue(e.target.value)}
                                onKeyDown={handleKeyDown}
                                autoFocus
                                variant="outlined"
                                InputProps={{ style: { height: '40px' } }}
                              />
                              <IconButton size="small" onClick={handleSaveField} disabled={editSubmitting} color="primary">
                                <CheckIcon fontSize="small" />
                              </IconButton>
                              <IconButton size="small" onClick={() => { setEditingField(null); setFieldValue(''); }} disabled={editSubmitting} color="error">
                                <CloseIcon fontSize="small" />
                              </IconButton>
                            </Box>
                          ) : (
                            <Typography variant="body2" sx={{ minHeight: 36, display: 'flex', alignItems: 'center', '&:hover': { backgroundColor: 'action.hover', cursor: 'pointer', borderRadius: 1 } }} onClick={() => handleStartEditing('age', patient.age)}>
                              {age || '—'}
                            </Typography>
                          )}
                        </Box>
                      </Grid>
                      <Grid item xs={12} md={4}>
                        <Box>
                          <Typography variant="caption" color="text.secondary" display="block">
                            {t('Date of Birth')}
                          </Typography>
                          {isEditing ? (
                            <TextField
                              type="date"
                              fullWidth
                              size="small"
                              margin="dense"
                              InputLabelProps={{ shrink: true }}
                              value={editedPatient.dateOfBirth ?? ''}
                              onChange={(e) => setEditedPatient({ ...editedPatient, dateOfBirth: e.target.value })}
                            />
                          ) : (
                            <Typography variant="body2" sx={{ minHeight: 36, display: 'flex', alignItems: 'center' }}>
                              {dateOfBirth || '—'}
                            </Typography>
                          )}
                        </Box>
                      </Grid>
                      <Grid item xs={12} md={4}>
                        <Box>
                          <Typography variant="caption" color="text.secondary" display="block">
                            {t('Gender')}
                          </Typography>
                          {isEditing ? (
                            <TextField
                              fullWidth
                              size="small"
                              margin="dense"
                              value={editedPatient.gender ?? ''}
                              onChange={(e) => setEditedPatient({ ...editedPatient, gender: e.target.value })}
                            />
                          ) : (
                            <Typography variant="body2" sx={{ minHeight: 36, display: 'flex', alignItems: 'center' }}>
                              {gender || '—'}
                            </Typography>
                          )}
                        </Box>
                      </Grid>

                      {/* Row 3 */}
                      <Grid item xs={12} md={4}>
                        <Box>
                          <Typography variant="caption" color="text.secondary" display="block">
                            {t('Card Date')}
                          </Typography>
                          <Typography variant="body2" sx={{ minHeight: 36, display: 'flex', alignItems: 'center' }}>
                            {cardDate}
                          </Typography>
                        </Box>
                      </Grid>
                      <Grid item xs={12} md={4}>
                        <Box>
                          <Typography variant="caption" color="text.secondary" display="block">
                            {t('Phone')}
                          </Typography>
                          {isEditing ? (
                            <TextField
                              fullWidth
                              size="small"
                              margin="dense"
                              value={editedPatient.phone ?? ''}
                              onChange={(e) => setEditedPatient({ ...editedPatient, phone: e.target.value })}
                            />
                          ) : (
                            <Typography variant="body2" sx={{ minHeight: 36, display: 'flex', alignItems: 'center' }}>
                              {phone || '—'}
                            </Typography>
                          )}
                        </Box>
                      </Grid>
                      <Grid item xs={12} md={4}>
                        <Box>
                          <Typography variant="caption" color="text.secondary" display="block">
                            {t('Email')}
                          </Typography>
                          {isEditing ? (
                            <TextField
                              fullWidth
                              size="small"
                              margin="dense"
                              type="email"
                              value={editedPatient.email ?? ''}
                              onChange={(e) => setEditedPatient({ ...editedPatient, email: e.target.value })}
                            />
                          ) : (
                            <Typography variant="body2" sx={{ minHeight: 36, display: 'flex', alignItems: 'center', wordBreak: 'break-all' }}>
                              {email || '—'}
                            </Typography>
                          )}
                        </Box>
                      </Grid>

                      {/* Full Width Row for Diagnosis */}
                      <Grid item xs={12}>
                        <Box sx={{ border: '1px solid #d32f2f', borderRadius: 1.5, p: 2, mt: 1, bgcolor: 'rgba(211, 47, 47, 0.02)' }}>
                          <Typography variant="caption" sx={{ color: '#d32f2f', fontWeight: 900, textTransform: 'uppercase', mb: 1, display: 'block' }}>
                            {t('Diagnosis')}
                          </Typography>
                          {isEditing ? (
                            <Autocomplete
                              multiple
                              freeSolo
                              options={epembaseisOptions}
                              value={editedPatient.diagnosis ? (Array.isArray(editedPatient.diagnosis) ? editedPatient.diagnosis : editedPatient.diagnosis.split(', ').filter(Boolean)) : []}
                              onChange={(e, newValue) => {
                                setEditedPatient({ ...editedPatient, diagnosis: newValue });
                              }}
                              renderInput={(params) => (
                                <TextField
                                  {...params}
                                  fullWidth
                                  size="small"
                                  margin="dense"
                                  sx={{ 
                                    bgcolor: 'background.paper',
                                    '& .MuiOutlinedInput-root': {
                                      p: 1
                                    }
                                  }}
                                />
                              )}
                            />
                          ) : (
                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, minHeight: 48, py: 1 }}>
                               {diagnosis !== '—' ? diagnosis.split(', ').map((d, idx) => (
                                 <Chip 
                                   key={idx} 
                                   label={d} 
                                   color="primary" 
                                   clickable
                                   onClick={() => window.open(`/query?diagnosis=${encodeURIComponent(d)}`, '_blank')}
                                   sx={{ fontWeight: 700, fontSize: '0.9rem' }}
                                 />
                               )) : '—'}
                            </Box>
                          )}
                        </Box>
                      </Grid>

                      {/* Remaining fields */}
                      <Grid item xs={12} md={4}>
                        <Box>
                          <Typography variant="caption" color="text.secondary" display="block">
                            {t('Address')}
                          </Typography>
                          {isEditing ? (
                            <TextField
                              fullWidth
                              size="small"
                              margin="dense"
                              multiline
                              minRows={2}
                              value={editedPatient.address ?? ''}
                              onChange={(e) => setEditedPatient({ ...editedPatient, address: e.target.value })}
                            />
                          ) : (
                            <Typography variant="body2" sx={{ minHeight: 36, display: 'flex', alignItems: 'center', whiteSpace: 'pre-line' }}>
                              {address || '—'}
                            </Typography>
                          )}
                        </Box>
                      </Grid>
                      <Grid item xs={12} md={4}>
                        <Box>
                          <Typography variant="caption" color="text.secondary" display="block">
                            {t('AM')}
                          </Typography>
                          {isEditing ? (
                            <TextField
                              fullWidth
                              size="small"
                              margin="dense"
                              value={editedPatient.am ?? ''}
                              onChange={(e) => setEditedPatient({ ...editedPatient, am: e.target.value })}
                            />
                          ) : (
                            <Typography variant="body2" sx={{ minHeight: 36, display: 'flex', alignItems: 'center' }}>
                              {am || '—'}
                            </Typography>
                          )}
                        </Box>
                      </Grid>
                      <Grid item xs={12} md={4}>
                        <Box>
                          <Typography variant="caption" color="text.secondary" display="block">
                            {t('Allergies')}
                          </Typography>
                          {isEditing ? (
                            <TextField
                              fullWidth
                              size="small"
                              margin="dense"
                              value={editedPatient.allergies ?? ''}
                              onChange={(e) => setEditedPatient({ ...editedPatient, allergies: e.target.value })}
                            />
                          ) : (
                            <Typography variant="body2" sx={{ minHeight: 36, display: 'flex', alignItems: 'center' }}>
                              {allergies || '—'}
                            </Typography>
                          )}
                        </Box>
                      </Grid>

                      <Grid item xs={12} md={4}>
                        <Box>
                          <Typography variant="caption" color="text.secondary" display="block">
                            {t('Death Info')}
                          </Typography>
                          {isEditing ? (
                            <TextField
                              fullWidth
                              size="small"
                              margin="dense"
                              value={editedPatient.death ?? ''}
                              onChange={(e) => setEditedPatient({ ...editedPatient, death: e.target.value })}
                            />
                          ) : (
                            <Typography variant="body2" sx={{ minHeight: 36, display: 'flex', alignItems: 'center' }}>
                              {death || '—'}
                            </Typography>
                          )}
                        </Box>
                      </Grid>
                      <Grid item xs={12} md={4}>
                        <Box>
                          <Typography variant="caption" color="text.secondary" display="block">
                            {t('Last Visit')}
                          </Typography>
                          {editingField === 'lastVisit' ? (
                            <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                              <TextField
                                type="date"
                                size="small"
                                fullWidth
                                margin="dense"
                                value={fieldValue}
                                onChange={(e) => setFieldValue(e.target.value)}
                                onKeyDown={handleKeyDown}
                                autoFocus
                                variant="outlined"
                                InputLabelProps={{ shrink: true }}
                                InputProps={{ style: { height: '40px' } }}
                              />
                              <IconButton size="small" onClick={handleSaveField} disabled={editSubmitting} color="primary">
                                <CheckIcon fontSize="small" />
                              </IconButton>
                              <IconButton size="small" onClick={() => { setEditingField(null); setFieldValue(''); }} disabled={editSubmitting} color="error">
                                <CloseIcon fontSize="small" />
                              </IconButton>
                            </Box>
                          ) : (
                            <Typography variant="body2" sx={{ minHeight: 36, display: 'flex', alignItems: 'center', '&:hover': { backgroundColor: 'action.hover', cursor: 'pointer', borderRadius: 1 } }} onClick={() => handleStartEditing('lastVisit', patient.last_visit)}>
                              {lastVisit || '—'}
                            </Typography>
                          )}
                        </Box>
                      </Grid>
                      <Grid item xs={12} md={4}>
                        <Box>
                          <Typography variant="caption" color="text.secondary" display="block">
                            {t('Updated')}
                          </Typography>
                          <Typography variant="body2" sx={{ minHeight: 36, display: 'flex', alignItems: 'center' }}>
                            {updatedAt || '—'}
                          </Typography>
                        </Box>
                      </Grid>

                      <Grid item xs={12}>
                        <Box mt={1.5}>
                          <Typography variant="caption" color="text.secondary" display="block" mb={0.5}>
                            {t('Notes')}
                          </Typography>
                          {isEditing ? (
                            <TextField
                              fullWidth
                              size="small"
                              multiline
                              minRows={3}
                              value={editedPatient.notes ?? patient.notes}
                              onChange={(e) => setEditedPatient({ ...editedPatient, notes: e.target.value })}
                            />
                          ) : (
                            <Box
                              sx={{
                                p: 1.5,
                                borderRadius: 1,
                                bgcolor: 'action.hover',
                                minHeight: 60,
                                border: '1px solid',
                                borderColor: 'divider',
                              }}
                            >
                              <Typography variant="body2" sx={{ whiteSpace: 'pre-line' }}>
                                {notes || '—'}
                              </Typography>
                            </Box>
                          )}
                        </Box>
                      </Grid>
                    </Grid>
                  </Box>
                </Box>
              </Stack>
            </Grid>

            <Grid item xs={12} md={6} xl={5}>
              <Box
                sx={{
                  borderRadius: 2,
                  border: (theme) => `1px solid ${theme.palette.divider}`,
                  p: 2.5,
                  minHeight: 400,
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 2,
                }}
              >
                <Stack direction="row" justifyContent="space-between" alignItems="center">
                  <Typography variant="subtitle1">{t('Patient File Preview')}</Typography>
                  <Stack direction="row" spacing={1} alignItems="center">
                    {previewContent?.signedUrl && (
                      <Tooltip title={isFullScreen ? t('Exit full screen') : t('View full screen')}>
                        <IconButton 
                          size="small" 
                          onClick={() => setIsFullScreen(!isFullScreen)}
                          color="primary"
                        >
                          {isFullScreen ? <FullscreenExitIcon /> : <FullscreenIcon />}
                        </IconButton>
                      </Tooltip>
                    )}
                    {filesLoading && <CircularProgress size={20} />}
                  </Stack>
                </Stack>

                {filesError && (
                  <Alert severity="error">{filesError}</Alert>
                )}

                <FormControl fullWidth size="small" disabled={filesLoading || patientFiles.length === 0}>
                  <InputLabel id="patient-file-select-label">{t('Select File')}</InputLabel>
                  <Select
                    labelId="patient-file-select-label"
                    label={t('Select File')}
                    value={selectedPreviewId ?? ''}
                    onChange={(event) => {
                      const fileId = event.target.value || null;
                      setSelectedPreviewId(fileId);
                      setPreviewContent(null);
                      setPreviewError('');
                    }}
                  >
                    <MenuItem value="">
                      <em>{t('None')}</em>
                    </MenuItem>
                    {patientFiles.map((file) => {
                      const mime = String(file.file_type || '').toLowerCase();
                      const label =
                        mime === 'application/pdf' ? 'PDF' :
                        mime.startsWith('image/') ? 'IMG' :
                        mime.startsWith('video/') ? 'VID' :
                        mime.startsWith('audio/') ? 'AUD' :
                        mime.includes('word') ? 'DOC' :
                        mime.includes('excel') || mime.includes('spreadsheet') ? 'XLS' : 'FILE';
                      const color =
                        mime === 'application/pdf' ? 'error' :
                        mime.startsWith('image/') ? 'success' :
                        mime.startsWith('video/') ? 'secondary' :
                        mime.startsWith('audio/') ? 'warning' :
                        mime.includes('word') ? 'primary' :
                        mime.includes('excel') || mime.includes('spreadsheet') ? 'success' : 'default';
                      return (
                        <MenuItem key={file.id_file ?? file.id} value={file.id_file ?? file.id}
                          sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                          <Box
                            component="span"
                            sx={{
                              fontSize: '0.6rem', fontWeight: 700, px: 0.7, py: 0.2,
                              borderRadius: '4px', flexShrink: 0,
                              bgcolor: `${color}.main`,
                              color: 'white',
                              lineHeight: 1.5,
                            }}
                          >
                            {label}
                          </Box>
                          <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                            {file.file_name ?? file.filename ?? `File ${file.id_file ?? file.id}`}
                          </span>
                        </MenuItem>
                      );
                    })}
                  </Select>
                </FormControl>

                {previewError && <Alert severity="error">{previewError}</Alert>}

                <Box
                  ref={previewRef}
                  sx={{
                    flex: 1,
                    minHeight: 420,
                    borderRadius: 1,
                    border: (theme) => `1px dashed ${theme.palette.divider}`,
                    display: 'flex',
                    alignItems: previewContent?.signedUrl ? 'stretch' : 'center',
                    justifyContent: previewContent?.signedUrl ? 'stretch' : 'center',
                    overflow: 'hidden',
                    position: 'relative',
                    '&:hover .fullscreen-button': { opacity: 1 },
                    '& .fullscreen-button': {
                      opacity: { xs: 1, sm: 0 }, 
                    }
                  }}
                >
                  {previewContent?.signedUrl ? (
                    <>
                      <FilePreviewRenderer file={previewContent} />
                      <IconButton
                        className="fullscreen-button"
                        size="small"
                        onClick={handleOpenFileFullscreen}
                        sx={{
                          position: 'absolute', 
                          top: 8, 
                          left: 8, // Moved to left to avoid overlapping viewer toolbars on the right
                          backgroundColor: 'rgba(0,0,0,0.5)', 
                          color: 'white',
                          transition: 'opacity 0.2s',
                          zIndex: 10,
                          '&:hover': { backgroundColor: 'rgba(0,0,0,0.75)', opacity: 1 },
                        }}
                      >
                        <FullscreenIcon />
                      </IconButton>
                    </>
                  ) : (
                    <Typography variant="body2" color="text.secondary" textAlign="center">
                      {t('Select a file to preview.')}
                    </Typography>
                  )}
                </Box>

                <Dialog
                  open={isFullScreen && !!previewContent?.signedUrl}
                  onClose={() => setIsFullScreen(false)}
                  maxWidth="xl"
                  fullWidth
                  fullScreen={isMobileView} 
                  sx={{
                    '& .MuiDialog-container': {
                      '& .MuiPaper-root': {
                        width: '100%', 
                        maxWidth: isMobileView ? '100%' : '92vw',
                        height: isMobileView ? '100%' : '92vh', 
                        maxHeight: isMobileView ? '100%' : '92vh',
                        m: 0, 
                        p: isMobileView ? 0 : 2,
                        borderRadius: isMobileView ? 0 : 1
                      },
                    },
                  }}
                >
                  <DialogTitle>
                    <Stack direction="row" justifyContent="space-between" alignItems="center">
                      <Typography variant="h6" noWrap sx={{ maxWidth: '80%' }}>
                        {previewContent?.file_name || t('File Preview')}
                      </Typography>
                      <IconButton onClick={() => setIsFullScreen(false)}>
                        <FullscreenExitIcon />
                      </IconButton>
                    </Stack>
                  </DialogTitle>
                  <DialogContent sx={{ p: 0, display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0, height: '100%' }}>
                    <Box sx={{ flex: 1, minHeight: 0, overflow: 'auto', display: 'flex', alignItems: 'stretch', justifyContent: 'center' }}>
                      <Box sx={{ width: '100%', height: '100%', overflow: 'hidden', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        {previewContent && <FilePreviewRenderer file={previewContent} fullscreen />}
                      </Box>
                    </Box>
                    {previewContent?.description && (
                      <Box sx={{ p: 2, borderTop: 1, borderColor: 'divider' }}>
                        <Typography variant="body2">{previewContent.description}</Typography>
                      </Box>
                    )}
                  </DialogContent>
                </Dialog>

                {previewContent && (
                  <Typography variant="caption" color="text.secondary" sx={{ textAlign: 'center' }}>
                    {previewContent.description || previewContent.file_name || 'No description'}
                  </Typography>
                )}
              </Box>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      <Box 
        ref={tabsSectionRef}
        sx={{ flex: 1, minHeight: 400, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}
      >
        <Card sx={{ flex: 1, display: 'flex', flexDirection: 'column', minHeight: 0, overflow: 'hidden' }}>
          <Tabs
            value={activeTab}
            onChange={handleTabChange}
            textColor="primary"
            indicatorColor="primary"
            variant="scrollable"
            scrollButtons="auto"
            allowScrollButtonsMobile
            sx={{
              borderBottom: 1,
              borderColor: 'divider',
              flexShrink: 0,
              bgcolor: 'background.paper',
              px: { xs: 1.5, md: 2 },
              pt: 1,
            }}
          >
            <Tab
              label={t('1. Medical History')}
              sx={{
                minHeight: { xs: 48, sm: 64 },
                fontSize: { xs: '0.8rem', sm: '0.95rem' },
                fontWeight: 600,
                textTransform: 'none',
                px: { xs: 1, md: 2.5 },
              }}
            />
            <Tab
              label={t('2. Visits')}
              sx={{
                minHeight: 64,
                fontSize: '0.95rem',
                fontWeight: 600,
                textTransform: 'none',
                px: { xs: 1.5, md: 2.5 },
              }}
            />
            <Tab
              label={t('3. Operations')}
              sx={{
                minHeight: 64,
                fontSize: '0.95rem',
                fontWeight: 600,
                textTransform: 'none',
                px: { xs: 1.5, md: 2.5 },
              }}
            />
            <Tab
              label={t('4. Medications')}
              sx={{
                minHeight: 64,
                fontSize: '0.95rem',
                fontWeight: 600,
                textTransform: 'none',
                px: { xs: 1.5, md: 2.5 },
              }}
            />
            <Tab
              label={t('5. Patient Files')}
              sx={{
                minHeight: 64,
                fontSize: '0.95rem',
                fontWeight: 600,
                textTransform: 'none',
                px: { xs: 1.5, md: 2.5 },
              }}
            />
            <Tab
              label={t('6. Gallery')}
              sx={{
                minHeight: 64,
                fontSize: '0.95rem',
                fontWeight: 600,
                textTransform: 'none',
                px: { xs: 1.5, md: 2.5 },
              }}
            />
          </Tabs>

          <Box sx={{ flex: 1, overflowX: 'hidden', minHeight: 0, p: 3 }}>
            {activeTab === 0 && (
              <MedHistoryTab 
                patientId={patientDbId} 
                patientName={displayName !== '—' ? displayName : ''}
                age={age}
                gender={gender}
                dateOfBirth={dateOfBirth}
                amka={amka}
                phone={phone}
                address={address}
                diagnosis={diagnosis}
              />
            )}
            {activeTab === 1 && (
              <VisitsTab
                patientId={patientDbId}
                apiClient={apiClient}
                patientName={displayName !== '—' ? displayName : ''}
                age={age}
                gender={gender}
                dateOfBirth={dateOfBirth}
                amka={amka}
                phone={phone}
                address={address}
                diagnosis={diagnosis}
              />
            )}
            {activeTab === 2 && (
              <OperationsTab
                patientId={patientDbId}
                apiClient={apiClient}
                patientName={displayName !== '—' ? displayName : ''}
                age={age}
                gender={gender}
                dateOfBirth={dateOfBirth}
                amka={amka}
                phone={phone}
                address={address}
                diagnosis={diagnosis}
              />
            )}
            {activeTab === 3 && (
              <MedicationsTab 
                patientId={patientDbId} 
                apiClient={apiClient} 
                patientName={displayName !== '—' ? displayName : ''}
                age={age}
                gender={gender}
                dateOfBirth={dateOfBirth}
                amka={amka}
                phone={phone}
                address={address}
                diagnosis={diagnosis}
              />
            )}
            {activeTab === 4 && (
              <PatientFilesTab
                patientId={patientDbId}
                apiClient={apiClient}
                onFilesChanged={() => {
                  reloadFiles();
                  invalidatePreviewCache();
                }}
                onPreviewRequest={async (file) => {
                  if (!file) {
                    setSelectedPreviewId(null);
                    setPreviewContent(null);
                    setPreviewError('');
                    return;
                  }

                  const fileId = file.id_file ?? file.id ?? null;
                  setSelectedPreviewId(fileId);
                  setPreviewError('');

                  if (file.signedUrl) {
                    setPreviewContent(file);
                    return;
                  }

                  if (!fileId) {
                    setPreviewContent(null);
                    return;
                  }

                  try {
                    const signed = await fetchPreviewSignedUrl(fileId);
                    if (!signed?.url) {
                      throw new Error('Signed URL unavailable');
                    }
                    setPreviewContent({
                      ...file,
                      signedUrl: signed.url,
                      signedFileType: signed.fileType ?? file.file_type,
                    });
                  } catch (previewErr) {
                    setPreviewError(previewErr.message ?? t('Failed to load preview'));
                  }
                }}
              />
            )}
            {activeTab === 5 && (
              <PatientGalleryTab 
                patientId={patientDbId}
                apiClient={apiClient}
                folderPath={serverAbsolutePath}
              />
            )}
          </Box>
        </Card>
      </Box>

      <PatientEditDialog
        open={editDialogOpen}
        patient={patient}
        onClose={() => setEditDialogOpen(false)}
        onSubmit={(values) => handleEditSubmit(values, patientDbId)}
        submitting={editSubmitting}
        error={editError}
      />
      <PatientEditDialog
        open={createOpen}
        onClose={handleCreateClose}
        onSubmit={handleCreateSubmit}
        submitting={createSubmitting}
        error={createError}
      />
      <Dialog
        open={deleteConfirmOpen}
        onClose={handleDeleteCancel}
        aria-labelledby="delete-dialog-title"
        aria-describedby="delete-dialog-description"
      >
        <DialogTitle id="delete-dialog-title">Delete Patient</DialogTitle>
        <DialogContent>
          <DialogContentText id="delete-dialog-description">
            Are you sure you want to delete {patient?.firstName} {patient?.lastName}? This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDeleteCancel} color="primary">
            Cancel
          </Button>
          <Button onClick={handleDeleteConfirm} color="error" autoFocus>
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
      <FullReportPreviewDialog
        open={fullReportDialogOpen}
        onClose={() => setFullReportDialogOpen(false)}
        reportHtml={fullReportHtml}
        patientName={displayName}
      />
    </Box>
  );
};

export default PatientDetailPage;
